<template>
  <div class="py-4">
    <!-- Header Block -->
    <div class="row mb-3">
      <div class="col-12 d-flex flex-wrap align-items-center justify-content-between">
        <div>
          <h4 class="mb-0 text-dark">Service Book Bulk Import</h4>
          <a v-if="previewData" href="javascript:void(0)" class="btn btn-primary rounded mt-2" @click="clearPreview">
            <b-icon icon="reply" rotate="0" flip-h="" class="mr-1"></b-icon>Back to Service Book upload
          </a>
        </div>
        <a :href="'/employee'" class="btn btn-primary rounded p-2 pr-4 pl-4">
          <b-icon icon="reply" rotate="0" flip-h="" class="mr-2"></b-icon>Back To List
        </a>
      </div>
    </div>

    <!-- Main Content Area with Overlay -->
    <b-overlay :show="showoverlay" rounded="sm">

      <!-- Upload Container -->
      <div class="mb-4" v-if="!previewData">
        <p class="mb-2">Upload the downloaded excel file after the modification.</p>
        <b-form-file
          id="datafile"
          ref="datafile"
          name="datafile"
          @change="uploadFile"
          accept=".xlsx,.xls"
          placeholder="No file chosen"
          drop-placeholder="Drop file here..."
        ></b-form-file>
        <div v-if="importedCount !== null" class="alert alert-success d-inline-block mt-4 px-4 py-2 rounded-pill">
          <b-icon icon="check-circle-fill" class="mr-1"></b-icon> {{ importedCount }} record(s) imported successfully
        </div>
      </div>

      <!-- Preview & Validation Interface -->
      <div v-if="previewData" class="card shadow-sm border-0" style="max-width: 100%; min-width: 0;">
        <div class="card-header border-0 d-flex flex-column flex-sm-row justify-content-between align-items-sm-center py-3 border-bottom">
          <span class="mb-3 mb-sm-0 text-dark">
            Processed Employee Data for Service Book
          </span>
        </div>

        <div class="card-body p-0">
          <b-tabs content-class="mt-3" nav-class="px-3 sb-tabs-nav">
            
            <!-- Tab 1: Worker Identification -->
            <b-tab active>
              <template #title>
                Worker Identification 
                <span class="badge badge-pill badge-secondary ml-1">{{ previewData.sheet1.length }}</span>
                <span v-if="sheetErrorsCount('sheet1') > 0" class="tab-error-text ml-1">
                  {{ sheetErrorsCount('sheet1') }} Error(s)
                </span>
              </template>
              
              <div class="p-3">
                <div class="alert alert-info py-2 px-3 small rounded-lg">
                  <b-icon icon="info-circle" class="mr-1"></b-icon> Rows with errors cannot be imported. Use the header checkbox to quickly select all valid rows. Note: Disabled fields won't be visible, also won't be updated as well.
                </div>
                
                <div class="table-responsive">
                  <table class="table table-striped table-bordered table-hover text-nowrap align-middle" style="font-size: 13px;">
                    <thead class="thead-light">
                      <tr>
                        <th class="text-center align-middle" width="50">
                          <input type="checkbox" :checked="isAllChecked('sheet1')" @change="toggleAll('sheet1')" :disabled="!hasValidRows('sheet1')" />
                        </th>
                        <th class="align-middle">Error Checking</th>
                        <th class="align-middle">ID</th>
                        <th class="align-middle">Employee Name</th>
                        <th class="align-middle">Worker's Name (BN)</th>
                        <th class="align-middle">Father's Name (EN)</th>
                        <th class="align-middle">Father's Name (BN)</th>
                        <th class="align-middle">Mother's Name (EN)</th>
                        <th class="align-middle">Mother's Name (BN)</th>
                        <th class="align-middle">Spouse's Name (BN) (if applicable)</th>
                        <th class="align-middle">Present Address (BN)</th>
                        <th class="align-middle">Permanent Village (EN)</th>
                        <th class="align-middle">Permanent Village (BN)</th>
                        <th class="align-middle">Permanent Road (EN)</th>
                        <th class="align-middle">Permanent Road (BN)</th>
                        <th class="align-middle">Permanent Post Office (EN)</th>
                        <th class="align-middle">Permanent Post Office (BN)</th>
                        <th class="align-middle">Permanent Police Station (EN)</th>
                        <th class="align-middle">Permanent Police Station (BN)</th>
                        <th class="align-middle">Permanent Subdistrict (EN)</th>
                        <th class="align-middle">Permanent Subdistrict (BN)</th>
                        <th class="align-middle">Permanent District (EN)</th>
                        <th class="align-middle">Permanent District (BN)</th>
                        <th class="align-middle">Education (EN)</th>
                        <th class="align-middle">Education (BN)</th>
                        <th class="align-middle">Training / Special Skills (English)</th>
                        <th class="align-middle">Training / Special Skills (Bangla)</th>
                        <th class="align-middle">Height</th>
                        <th class="align-middle">Identification Mark (EN)</th>
                        <th class="align-middle">Identification Mark (BN)</th>
                        <th class="align-middle">Opening Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in previewData.sheet1" :key="row._uid">
                        <td class="text-center align-middle">
                          <input type="checkbox" v-model="row.selected" :disabled="row.status === 'Error'" />
                        </td>
                        <td class="align-middle">
                          <a v-if="row.status === 'Error'" href="javascript:void(0)" class="error-link" @click="showErrorsModal(row)">ERROR!</a>
                          <span v-else class="ok-text">OK!</span>
                        </td>
                        <td class="align-middle text-secondary">{{ row.employee_id }}</td>
                        <td class="align-middle">{{ row.employee_name }}</td>
                        <td class="align-middle">{{ row.worker_name_bn }}</td>
                        <td class="align-middle">{{ row.father_name_en }}</td>
                        <td class="align-middle">{{ row.father_name_bn }}</td>
                        <td class="align-middle">{{ row.mother_name_en }}</td>
                        <td class="align-middle">{{ row.mother_name_bn }}</td>
                        <td class="align-middle">{{ row.spouse_name_bn }}</td>
                        <td class="align-middle">{{ row.present_address_bn }}</td>
                        <td class="align-middle">{{ row.permanent_village_en }}</td>
                        <td class="align-middle">{{ row.permanent_village_bn }}</td>
                        <td class="align-middle">{{ row.permanent_road_en }}</td>
                        <td class="align-middle">{{ row.permanent_road_bn }}</td>
                        <td class="align-middle">{{ row.permanent_post_office_en }}</td>
                        <td class="align-middle">{{ row.permanent_post_office_bn }}</td>
                        <td class="align-middle">{{ row.permanent_police_station_en }}</td>
                        <td class="align-middle">{{ row.permanent_police_station_bn }}</td>
                        <td class="align-middle">{{ row.permanent_sub_district_en }}</td>
                        <td class="align-middle">{{ row.permanent_sub_district_bn }}</td>
                        <td class="align-middle">{{ row.permanent_district_en }}</td>
                        <td class="align-middle">{{ row.permanent_district_bn }}</td>
                        <td class="align-middle">{{ row.educational_qualification_en }}</td>
                        <td class="align-middle">{{ row.educational_qualification_bn }}</td>
                        <td class="align-middle">{{ row.training_special_skills_en }}</td>
                        <td class="align-middle">{{ row.training_special_skills_bn }}</td>
                        <td class="align-middle">{{ row.height }}</td>
                        <td class="align-middle">{{ row.identification_mark_en }}</td>
                        <td class="align-middle">{{ row.identification_mark_bn }}</td>
                        <td class="align-middle">{{ row.opening_date }}</td>
                      </tr>
                      <tr v-if="previewData.sheet1.length === 0">
                        <td colspan="28" class="text-center text-muted py-4">No worker identification data found in the uploaded sheet.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </b-tab>

            <!-- Tab 2: Employer & Employment -->
            <b-tab>
              <template #title>
                Employer &amp; Employment
                <span class="badge badge-pill badge-secondary ml-1">{{ previewData.sheet2.filter(r => !r.is_active).length }}</span>
                <span v-if="sheetErrorsCount('sheet2') > 0" class="tab-error-text ml-1">
                  {{ sheetErrorsCount('sheet2') }} Error(s)
                </span>
              </template>
              
              <div class="p-3">
                <div class="alert alert-info py-2 px-3 small rounded-lg">
                  <b-icon icon="info-circle" class="mr-1"></b-icon> Validates chronological alignment. Overlapping employment dates or missing resignation dates for historical roles are flagged as errors.
                </div>

                <div class="table-responsive">
                  <table class="table table-striped table-bordered table-hover text-nowrap align-middle" style="font-size: 13px;">
                    <thead class="thead-light">
                      <tr>
                        <th class="text-center align-middle" width="50">
                          <input type="checkbox" :checked="isAllChecked('sheet2')" @change="toggleAll('sheet2')" :disabled="!hasValidRows('sheet2')" />
                        </th>
                        <th class="align-middle">Error Checking</th>
                        <th class="align-middle">ID</th>
                        <th class="align-middle">Employee Name</th>
                        <th class="align-middle">Factory Name &amp; Address (EN)</th>
                        <th class="align-middle">Factory Name &amp; Address (BN)</th>
                        <th class="align-middle">Owner/Management (EN)</th>
                        <th class="align-middle">Owner/Management (BN)</th>
                        <th class="align-middle">Joining Date</th>
                        <th class="align-middle">Resignation Date</th>
                        <th class="align-middle">Reason of Termination (EN)</th>
                        <th class="align-middle">Reason of Termination (BN)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, index) in previewData.sheet2" :key="row._uid" :class="{'border-group-end': index === previewData.sheet2.length - 1 || row.employee_id !== previewData.sheet2[index + 1].employee_id}">
                        <td class="text-center align-middle">
                          <input type="checkbox" v-model="row.selected" :disabled="row.status === 'Error' || row.is_active" />
                        </td>
                        <td class="align-middle">
                          <a v-if="row.status === 'Error'" href="javascript:void(0)" class="error-link" @click="showErrorsModal(row)">ERROR</a>
                          <span v-else class="ok-text">OK</span>
                        </td>
                        <td class="align-middle text-secondary">{{ row.employee_id }}</td>
                        <td class="align-middle">{{ row.employee_name }}</td>
                        <td class="align-middle" style="white-space: normal; min-width: 200px; max-width: 300px;">{{ row.factory_name_address_en }}</td>
                        <td class="align-middle" style="white-space: normal; min-width: 200px; max-width: 300px;">{{ row.factory_name_address_bn }}</td>
                        <td class="align-middle">{{ row.owner_authority_name_en }}</td>
                        <td class="align-middle">{{ row.owner_authority_name_bn }}</td>
                        <td class="align-middle">{{ row.joining_date }}</td>
                        <td class="align-middle">{{ row.status === 'Valid' && row.is_active ? 'Active' : row.resignation_termination_date }}</td>
                        <td class="align-middle" style="white-space: normal; min-width: 200px; max-width: 300px;">{{ row.reason_of_termination_en }}</td>
                        <td class="align-middle" style="white-space: normal; min-width: 200px; max-width: 300px;">{{ row.reason_of_termination_bn }}</td>
                      </tr>
                      <tr v-if="previewData.sheet2.length === 0">
                        <td colspan="12" class="text-center text-muted py-4">No employment details found in the uploaded sheet.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </b-tab>

            <!-- Tab 3: Behavioral Record -->
            <b-tab>
              <template #title>
                Behavioral Record 
                <span class="badge badge-pill badge-secondary ml-1">{{ previewData.sheet3.length }}</span>
                <span v-if="sheetErrorsCount('sheet3') > 0" class="tab-error-text ml-1">
                  {{ sheetErrorsCount('sheet3') }} Error(s)
                </span>
              </template>
              
              <div class="p-3">
                <div class="alert alert-info py-2 px-3 small rounded-lg">
                  <b-icon icon="info-circle" class="mr-1"></b-icon> Validates behavioral record entries. Dates must be in the past or present, and descriptions must match corresponding language types.
                </div>

                <div class="table-responsive">
                  <table class="table table-striped table-bordered table-hover text-nowrap align-middle" style="font-size: 13px;">
                    <thead class="thead-light">
                      <tr>
                        <th class="text-center align-middle" width="50">
                          <input type="checkbox" :checked="isAllChecked('sheet3')" @change="toggleAll('sheet3')" :disabled="!hasValidRows('sheet3')" />
                        </th>
                        <th class="align-middle">Error Checking</th>
                        <th class="align-middle">ID</th>
                        <th class="align-middle">Employee Name</th>
                        <th class="align-middle">Date</th>
                        <th class="align-middle">Description (EN)</th>
                        <th class="align-middle">Description (BN)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, index) in previewData.sheet3" :key="row._uid" :class="{'border-group-end': index === previewData.sheet3.length - 1 || row.employee_id !== previewData.sheet3[index + 1].employee_id}">
                        <td class="text-center align-middle">
                          <input type="checkbox" v-model="row.selected" :disabled="row.status === 'Error'" />
                        </td>
                        <td class="align-middle">
                          <a v-if="row.status === 'Error'" href="javascript:void(0)" class="error-link" @click="showErrorsModal(row)">ERROR</a>
                          <span v-else class="ok-text">OK</span>
                        </td>
                        <td class="align-middle text-secondary">{{ row.employee_id }}</td>
                        <td class="align-middle">{{ row.employee_name }}</td>
                        <td class="align-middle">{{ row.date }}</td>
                        <td class="align-middle" style="white-space: normal; min-width: 200px; max-width: 300px;">{{ row.description_en }}</td>
                        <td class="align-middle" style="white-space: normal; min-width: 200px; max-width: 300px;">{{ row.description_bn }}</td>
                      </tr>
                      <tr v-if="previewData.sheet3.length === 0">
                        <td colspan="7" class="text-center text-muted py-4">No behavioral records found in the uploaded sheet.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </b-tab>

          </b-tabs>

          <!-- Action Buttons at bottom left -->
          <div class="card-footer border-0 pt-0 pb-4 px-4 d-flex justify-content-start">
            <b-button variant="success" class="btn btn-primary rounded mr-2" :disabled="totalSelected === 0" @click="confirmImport">
              <b-icon icon="check-circle" class="mr-1"></b-icon> Import Checked ({{ totalSelected }} selected)
            </b-button>
          </div>
        </div>
      </div>

    </b-overlay>

    <!-- Row Validation Errors -->
    <b-modal
      v-model="showErrorModal"
      title="Validation Error(s)"
      header-bg-variant="danger"
      header-text-variant="white"
      modal-class="sb-error-modal"
      ok-only
      ok-title="Close"
      ok-variant="secondary"
      @hidden="errorRow = null"
    >
      <template v-if="errorRow">
        <p class="mb-3">
          {{ errorRow.employee_name }}
        </p>
        <ul class="pl-3 mb-0">
          <li v-for="(err, index) in errorRow.errors" :key="index" class="mb-2 text-danger">
            {{ err }}
          </li>
        </ul>
      </template>
    </b-modal>
  </div>
</template>

<script>
// Added by Tareq Sifat 2026-08-02 — mirrors ServiceBookImportExportController::MAX_UPLOAD_BYTES,
// which is the authority; keep the two in step. Checking it here as well is not belt-and-braces:
// nginx rejects an oversized body itself, before the request ever reaches PHP, and answers with its
// own 413 HTML page — so the upload failed with a raw "413 Request Entity Too Large" and no toast.
const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

export default {
  data() {
    return {
      previewData: null,
      importedCount: null,
      showoverlay: false,
      showErrorModal: false,
      errorRow: null,
    }
  },
  computed: {
    totalSelected() {
      if (!this.previewData) return 0;
      let count = 0;
      count += this.previewData.sheet1.filter(r => r.selected).length;
      count += this.previewData.sheet2.filter(r => r.selected).length;
      count += this.previewData.sheet3.filter(r => r.selected).length;
      return count;
    }
  },
  methods: {

    uploadFile(event) {
      if (!this.checkNet()) return false;

      const file = event.target.files[0];
      if (!file) return false;

      // Added by Tareq Sifat 2026-08-02 — stop an oversized file here rather than sending it and
      // letting nginx answer 413 with a page the error handler below cannot read a message out of.
      if (file.size > MAX_UPLOAD_BYTES) {
        this.showFileTooLargeToast(file.size);
        if (this.$refs.datafile) {
          this.$refs.datafile.reset();
        }
        return false;
      }

      this.showoverlay = true;
      this.importedCount = null;

      const config = {
        headers: { 'content-type': 'multipart/form-data' }
      }
      const data = new FormData();
      data.append('datafile', file);

      axios.post(this.$api_url + '/admin-service-book/import-export/upload', data, config)
        .then(function (response) {
          this.showoverlay = false;
          
          this.previewData = {
            sheet1: (response.data.sheet1 || []).map((row, index) => ({ ...row, _uid: 'sheet1_' + index, selected: row.status === 'Valid' })),
            sheet2: (response.data.sheet2 || []).map((row, index) => ({ ...row, _uid: 'sheet2_' + index, selected: row.status === 'Valid' && !row.is_active })),
            sheet3: (response.data.sheet3 || []).map((row, index) => ({ ...row, _uid: 'sheet3_' + index, selected: row.status === 'Valid' }))
          };

          this.$bvToast.toast('File uploaded', {
            title: 'Job On Progress',
            variant: 'info',
            solid: true
          });
        }.bind(this))
        .catch(function (error) {
          this.showoverlay = false;
          if (this.$refs.datafile) {
            this.$refs.datafile.reset();
          }
          // Added by Tareq Sifat 2026-08-02 — a 413 still reaches here if the server's own body
          // limit is set below MAX_UPLOAD_BYTES. Its body is nginx's HTML page, not our JSON, so it
          // needs its own message instead of falling through to serverError().
          if (error.response && error.response.status === 413) {
            this.showFileTooLargeToast(file.size);
            return;
          }
          if (error.response && (error.response.status === 422 || error.response.status === 424)) {
            const errorMsg = error.response.status === 424
              ? 'Please upload the correct template'
              : (error.response.data.error || error.response.data.message || 'Validation or format error occurred.');
            this.$bvToast.toast(errorMsg, {
              title: error.response.status === 422 ? 'Validation Error' : 'Format Mismatch',
              variant: 'danger',
              solid: true,
              toaster: 'b-toaster-bottom-left'
            });
          } else {
            this.serverError(error);
          }
        }.bind(this));
    },
    confirmImport() {
      if (!this.checkNet()) return false;
      this.showoverlay = true;

      const payload = {
        sheet1: this.previewData.sheet1.filter(r => r.selected),
        sheet2: this.previewData.sheet2.filter(r => r.selected),
        sheet3: this.previewData.sheet3.filter(r => r.selected)
      };

      axios.post(this.$api_url + '/admin-service-book/import-export/import', payload)
        .then(function (response) {
          this.showoverlay = false;
          this.importedCount = response.data.imported;
          this.previewData = null;

          this.$bvToast.toast(`${this.importedCount} record(s) imported successfully`, {
            title: 'Import Success',
            variant: 'success',
            solid: true
          });

          setTimeout(function() {
            window.location.href = '/employee';
          }, 2000);
        }.bind(this))
        .catch(function (error) {
          this.showoverlay = false;
          if (error.response && (error.response.status === 422 || error.response.status === 424)) {
            const errorMsg = error.response.status === 424
              ? 'Please upload the correct template'
              : (error.response.data.error || error.response.data.message || 'Validation or format error occurred.');
            this.$bvToast.toast(errorMsg, {
              title: error.response.status === 422 ? 'Validation Error' : 'Format Mismatch',
              variant: 'danger',
              solid: true,
              toaster: 'b-toaster-bottom-left'
            });
          } else {
            this.serverError(error);
          }
        }.bind(this));
    },
    clearPreview() {
      this.previewData = null;
      this.importedCount = null;
    },
    sheetErrorsCount(sheetName) {
      if (!this.previewData || !this.previewData[sheetName]) return 0;
      return this.previewData[sheetName].filter(r => r.status === 'Error').length;
    },
    isAllChecked(sheetName) {
      if (!this.previewData || !this.previewData[sheetName]) return false;
      const validRows = this.previewData[sheetName].filter(r => r.status === 'Valid' && !r.is_active);
      if (validRows.length === 0) return false;
      return validRows.every(r => r.selected);
    },
    toggleAll(sheetName) {
      if (!this.previewData || !this.previewData[sheetName]) return;
      const allChecked = this.isAllChecked(sheetName);
      this.previewData[sheetName].forEach(r => {
        if (r.status === 'Valid' && !r.is_active) {
          r.selected = !allChecked;
        }
      });
    },
    hasValidRows(sheetName) {
      if (!this.previewData || !this.previewData[sheetName]) return false;
      return this.previewData[sheetName].some(r => r.status === 'Valid' && !r.is_active);
    },
    showErrorsModal(row) {
      if (row.errors && row.errors.length > 0) {
        this.errorRow = row;
        this.showErrorModal = true;
      }
    },
    // Added by Tareq Sifat 2026-08-02
    showFileTooLargeToast(size) {
      const limitMb = Math.round(MAX_UPLOAD_BYTES / (1024 * 1024));
      const actualMb = (size / (1024 * 1024)).toFixed(1);
      this.$bvToast.toast(
        `The selected file is ${actualMb} MB. Please upload an Excel file of ${limitMb} MB or less.`,
        {
          title: 'File Too Large',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-left'
        }
      );
    }
  },

}
</script>

<style scoped>
.error-link,
.error-link:hover,
.error-link:focus {
  color: red;
  font-size: 0.8em;
  text-decoration: none;
}
.ok-text {
  color: green;
  font-size: 0.8em;
}
.table-responsive {
  max-height: 550px;
  overflow-y: auto !important;
  overflow-x: auto !important;
  display: block !important;
  width: 100% !important;
  max-width: 100% !important;
}
thead th {
  position: sticky;
  top: 0;
  background-color: #f8f9fa;
  z-index: 1;
}
.border-group-end td {
  border-bottom: 2.5px solid #adb5bd !important;
}
</style>

<!-- Not scoped: b-tabs/b-modal render their own markup, outside this component's scope attribute. -->
<style>
.sb-tabs-nav .nav-link {
  color: #888;
  font-size: 18px;
}
.sb-tabs-nav .nav-link:hover,
.sb-tabs-nav .nav-link:focus {
  color: #0892a4;
}
.sb-tabs-nav .nav-link.active {
  color: #0892a4;
}
.sb-tabs-nav .tab-error-text {
  color: red;
  font-size: 0.8em;
}
.sb-error-modal .modal-content {
  border-top-left-radius: 0.75rem;
  border-top-right-radius: 0.75rem;
  overflow: hidden;
}
.sb-error-modal .modal-header {
  border-top-left-radius: 0.75rem;
  border-top-right-radius: 0.75rem;
}
</style>
